import { useEffect, useState, type ReactNode } from 'react';
import { AppLoadingSplash } from '@eureca/ui';
import { setAccessTokenProvider, setUnauthorizedHandler } from '@eureca/client-api-sdk/client';
import { ensureKeycloakInitialized, getKeycloak } from './keycloak-instance';

export function KeycloakGate({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const authenticated = await ensureKeycloakInitialized();
        if (cancelled) return;

        if (!authenticated) {
          setAccessTokenProvider(null);
          setReady(true);
          return;
        }

        const keycloak = getKeycloak();

        setAccessTokenProvider(async () => {
          try {
            await keycloak.updateToken(30);
            return keycloak.token ?? undefined;
          } catch {
            keycloak.login();
            return undefined;
          }
        });

        setUnauthorizedHandler(() => {
          keycloak.login({ redirectUri: window.location.href });
        });

        setReady(true);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Keycloak init failed');
        }
      }
    })();

    return () => {
      cancelled = true;
      setAccessTokenProvider(null);
      setUnauthorizedHandler(null);
    };
  }, []);

  if (error) {
    return (
      <div style={{ padding: '2rem', fontFamily: 'system-ui', maxWidth: '28rem' }}>
        <p>Authentication error: {error}</p>
      </div>
    );
  }

  if (!ready) {
    // Shared global loading skeleton (pulsing Eureca mark) while Keycloak boots.
    return <AppLoadingSplash label="Entrando" />;
  }

  return <>{children}</>;
}
